import ExcelJS from 'exceljs';
import type { Device, SleepData, Health, Company, Group } from '../types';

interface GroupedDeviceData {
  [companyId: string]: {
    company: Company | null;
    groups: {
      [groupId: string]: {
        group: Group | null;
        devices: Device[];
      };
    };
  };
}

interface ExportData {
  groupedDevices: GroupedDeviceData;
  sleepDataMap: Map<string, SleepData>;
  healthDataMap: Map<string, Health[]>;
  deviceShiftMap: Map<string, string>;
  followupMap: Map<string, any>;
  selectedDate: string;
  minSleepDuration: number;
}

const getStressLevelText = (value: number): string => {
  if (value === 0) return 'Relax';
  if (value <= 30) return 'Relaxed';
  if (value <= 50) return 'Low Stress';
  if (value < 75) return 'Medium Stress';
  return 'High Stress';
};

const getSleepStatusText = (sleepTimeSeconds: number, minSleepDuration: number): string => {
  const minSleepInSeconds = minSleepDuration * 60;
  if (sleepTimeSeconds === 0) return 'No Sleep Data';
  if (sleepTimeSeconds >= minSleepInSeconds) return 'Normal Sleep';
  return 'Abnormal Sleep';
};

const formatTime = (timeString: string | undefined | null): string => {
  if (!timeString) return '-';
  try {
    const date = new Date(timeString);
    const hours = date.getUTCHours().toString().padStart(2, '0');
    const minutes = date.getUTCMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  } catch {
    return '-';
  }
};

const formatSleepDuration = (seconds: number): string => {
  if (seconds === 0) return 'No Data';
  const hours = Math.floor(seconds / 3600);
  const mins = Math.round((seconds % 3600) / 60);
  return `${hours}h ${mins}m`;
};

export async function exportToExcel(data: ExportData): Promise<void> {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Sleep Monitoring Report');

  // Set column widths
  worksheet.columns = [
    { header: 'ID Employee', key: 'idEmployee', width: 12 },
    { header: 'Name', key: 'name', width: 34 },
    { header: 'ID Device', key: 'deviceName', width: 12 },
    { header: 'Duty', key: 'duty', width: 14 },
    { header: 'Shift', key: 'shift', width: 12 },
    { header: 'Sleep Status', key: 'sleepStatus', width: 18 },
    { header: 'Start Time', key: 'startTime', width: 12 },
    { header: 'End Time', key: 'endTime', width: 12 },
    { header: 'Total Sleep', key: 'totalSleep', width: 12 },
    { header: 'Sleep Quality', key: 'sleepQuality', width: 14 },
    { header: 'Heart Rate (bpm)', key: 'heartRate', width: 16 },
    { header: 'SpO2 (%)', key: 'bloodOxygen', width: 12 },
    { header: 'Stress Level', key: 'stressLevel', width: 15 },
    { header: 'Follow Up', key: 'followUp', width: 15 },
    { header: 'Notes', key: 'notes', width: 30 },
  ];

  // Style header row
  const headerRow = worksheet.getRow(1);
  headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
  headerRow.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FF1F2937' },
  };
  headerRow.alignment = { horizontal: 'center', vertical: 'top', wrapText: true };

  let currentRow = 2;
  let totalDevices = 0;
  let normalSleep = 0;
  let abnormalSleep = 0;
  let noData = 0;

  // Iterate through grouped data
  Object.entries(data.groupedDevices).forEach(([companyId, companyData]) => {
    // Skip unassigned company
    if (companyId === 'unassigned') return;
    
    Object.entries(companyData.groups).forEach(([groupId, groupData]) => {
      // Skip unassigned group
      if (groupId === 'unassigned') return;
      
      // Add device rows
      groupData.devices.forEach((device) => {
        const sleepData = data.sleepDataMap.get(device.id);
        const healthData = data.healthDataMap.get(device.id) || [];
        const sleepTimeSeconds = sleepData?.sleepTime || 0;

        const heartRateData = healthData
          .filter((h) => h.data_type === 'heart_rate')
          .map((h) => h.string_val);
        const bloodOxygenData = healthData
          .filter((h) => h.data_type === 'blood_oxygen')
          .map((h) => h.string_val);
        const stressLevelData = healthData
          .filter((h) => h.data_type === 'pressure')
          .map((h) => h.string_val);

        const avgHeartRate =
          heartRateData.length > 0
            ? Math.round(heartRateData.reduce((a, b) => a + b, 0) / heartRateData.length)
            : 0;
        const avgBloodOxygen =
          bloodOxygenData.length > 0
            ? Math.round(bloodOxygenData.reduce((a, b) => a + b, 0) / bloodOxygenData.length)
            : 0;
        const avgStressLevel =
          stressLevelData.length > 0
            ? Math.round(stressLevelData.reduce((a, b) => a + b, 0) / stressLevelData.length)
            : 0;

        const shiftType = data.deviceShiftMap.get(device.id);
        let shiftDisplay = '-';
        if (shiftType === 'fullday') shiftDisplay = 'Full Day';
        else if (shiftType === 'day') shiftDisplay = 'Day Shift';
        else if (shiftType === 'night') shiftDisplay = 'Night Shift';
        else if (shiftType) shiftDisplay = 'Off';

        const followup = device.idEmployee ? data.followupMap.get(device.idEmployee) : null;
        const followupType = followup?.followup_type || '-';
        const notes = (followup?.notes as string) || '-';

        const sleepStatus = getSleepStatusText(sleepTimeSeconds, data.minSleepDuration);

        const row = worksheet.getRow(currentRow);
        row.getCell('idEmployee').value = device.employee?.NIP || '-';
        row.getCell('name').value = device.employee?.Nama || device.name || 'Unnamed';
        row.getCell('deviceName').value = device.name || '-';
        row.getCell('duty').value = device.employee?.Jabatan || '-';
        row.getCell('shift').value = shiftDisplay;
        row.getCell('sleepStatus').value = sleepStatus;
        row.getCell('startTime').value = formatTime(sleepData?.startTime);
        row.getCell('endTime').value = formatTime(sleepData?.endTime);
        row.getCell('totalSleep').value = formatSleepDuration(sleepTimeSeconds);
        row.getCell('sleepQuality').value = sleepData?.sleepQuality ? `${Math.round(sleepData.sleepQuality * 10000)}%` : '-';
        row.getCell('heartRate').value = avgHeartRate > 0 ? avgHeartRate : '-';
        row.getCell('bloodOxygen').value = avgBloodOxygen > 0 ? avgBloodOxygen : '-';
        row.getCell('stressLevel').value = avgStressLevel > 0 ? getStressLevelText(avgStressLevel) : '-';
        row.getCell('followUp').value = followupType;
        row.getCell('notes').value = notes;

        // Style data rows
        row.alignment = { horizontal: 'left', vertical: 'top', wrapText: true };

        // Alternate row colors
        if (currentRow % 2 === 0) {
          row.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFF3F4F6' },
          };
        }

        // Highlight sleep status with colors
        if (sleepStatus === 'Normal Sleep') {
          row.getCell('sleepStatus').fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFE8F5E9' },
          };
          row.getCell('sleepStatus').font = { color: { argb: 'FF2E7D32' }, bold: true };
        } else if (sleepStatus === 'Abnormal Sleep') {
          row.getCell('sleepStatus').fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFFFE5E5' },
          };
          row.getCell('sleepStatus').font = { color: { argb: 'FFDC2626' }, bold: true };
        } else if (sleepStatus === 'No Sleep Data') {
          row.getCell('sleepStatus').fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFF5F5F5' },
          };
          row.getCell('sleepStatus').font = { color: { argb: 'FF616161' }, bold: true };
        }

        // Count statistics
        totalDevices++;
        const minSleepInSeconds = data.minSleepDuration * 60;
        if (sleepTimeSeconds === 0) {
          noData++;
        } else if (sleepTimeSeconds >= minSleepInSeconds) {
          normalSleep++;
        } else {
          abnormalSleep++;
        }

        currentRow++;
      });
    });
  });

  // Get current date and time with local timezone
  const now = new Date();
  const dateTimeString = now.toLocaleString('id-ID', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  // Add summary section
  const summaryStartRow = currentRow + 2;
  worksheet.getCell(`A${summaryStartRow}`).value = 'Report Summary';
  worksheet.getCell(`A${summaryStartRow}`).font = { bold: true, size: 12 };

  worksheet.getCell(`A${summaryStartRow + 1}`).value = 'Report Date:';
  worksheet.getCell(`B${summaryStartRow + 1}`).value = dateTimeString;

  worksheet.getCell(`A${summaryStartRow + 2}`).value = 'Total Employees:';
  worksheet.getCell(`B${summaryStartRow + 2}`).value = totalDevices;

  worksheet.getCell(`A${summaryStartRow + 3}`).value = 'Normal Sleep:';
  worksheet.getCell(`B${summaryStartRow + 3}`).value = normalSleep;

  worksheet.getCell(`A${summaryStartRow + 4}`).value = 'Abnormal Sleep:';
  worksheet.getCell(`B${summaryStartRow + 4}`).value = abnormalSleep;

  worksheet.getCell(`A${summaryStartRow + 5}`).value = 'No Data:';
  worksheet.getCell(`B${summaryStartRow + 5}`).value = noData;

  // Generate file with timestamp
  const fileTimestamp = now.toISOString().split('T')[0];
  const fileTime = now.toLocaleTimeString('id-ID', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).replace(/:/g, '-');

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  // Create download link
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `Sleep_Monitoring_Report_${fileTimestamp}_${fileTime}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
